// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { DealerModel, WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Mail from "@insite/mobius/Icons/Mail";
import Phone from "@insite/mobius/Icons/Phone";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ContactTypography from "@keller/App/Components/Common/ContactTypography";
import Fax from "@keller/XDL/Icons/Fax";
import mapLocation from "@keller/XDL/Icons/MapLocation";
import { color, iconLinkCommonStyles, mergeWithCss, xScreen } from "@keller/XDL/Utilities";
import React, { FC, useEffect } from "react";
import { css } from "styled-components";

interface OwnProps {
    isOpen?: boolean;
    dealer?: DealerModel | WarehouseModel;
    modalCloseHandler: () => void;
}

type Props = OwnProps;

export interface ShowroomModalStyles {
    showroomInfoModal?: ModalPresentationProps;
    modalContent: InjectableCss;
    locationMapIcon?: InjectableCss;
    showroomContactWrapper?: LinkPresentationProps;
    showroomInformation?: {
        showroomEmailAddress?: LinkPresentationProps;
        showroomPhoneNumber?: LinkPresentationProps;
        showroomFaxNumber?: LinkPresentationProps;
    };
}

export const showroomModalStyles: ShowroomModalStyles = {
    showroomInfoModal: {
        size: 420,
    },
    modalContent: {
        css: css`
            display: grid;
            grid-template-columns: minmax(60px, 100px) auto;
            gap: 20px;
            ${xScreen("lte", "xs")} {
                grid-template-columns: minmax(40px, 60px) auto;
            }
        `,
    },
    locationMapIcon: {
        css: css`
            &,
            & > svg {
                width: 100%;
                height: 100%;
            }
        `,
    },
    showroomContactWrapper: {
        css: css`
            font-weight: bold;
            font-size: 14px;
            padding-bottom: 8px;
            display: grid;
            grid-template-columns: 1fr;
            gap: 8px;

            &:hover {
                color: ${color("common.ui1")};
            }
        `,
    },
    showroomInformation: {
        showroomEmailAddress: {
            ...mergeWithCss(iconLinkCommonStyles(Mail), {
                css: css`
                    word-break: break-all; /* Break the email at any point */
                    overflow-wrap: break-word; /* Ensure wrapping if the text overflows */
                `,
            }),
        },
        showroomPhoneNumber: {
            ...mergeWithCss(iconLinkCommonStyles(Phone), {
                css: css`
                    word-break: break-all; /* Break the email at any point */
                    overflow-wrap: break-word; /* Ensure wrapping if the text overflows */
                `,
            }),
        },
        showroomFaxNumber: {
            ...mergeWithCss(iconLinkCommonStyles(Fax), {
                css: css`
                    word-break: break-all; /* Break the email at any point */
                    overflow-wrap: break-word; /* Ensure wrapping if the text overflows */
                `,
            }),
        },
    },
};

const styles = showroomModalStyles;

const ShowroomModal: FC<Props> = ({ isOpen, dealer, modalCloseHandler }) => {
    return (
        <Modal
            headline={translate("Showroom")}
            isOpen={isOpen}
            handleClose={modalCloseHandler}
            {...styles.showroomInfoModal}
        >
            <StyledWrapper {...styles.modalContent}>
                <Icon src={mapLocation} {...styles.locationMapIcon} />
                <StyledWrapper {...styles.showroomContactWrapper}>
                    {dealer?.properties?.showroomEmail && (
                        <ContactTypography
                            {...styles.showroomInformation?.showroomEmailAddress}
                            type="email"
                            targetValue={dealer.properties.showroomEmail}
                            icon={true}
                        >
                            {dealer.properties.showroomEmail}
                        </ContactTypography>
                    )}
                    {dealer?.properties.showroomPhoneNumber && (
                        <ContactTypography
                            {...styles.showroomInformation?.showroomPhoneNumber}
                            type="phone"
                            targetValue={dealer.properties.showroomPhoneNumber}
                            icon={true}
                        >
                            {dealer.properties.showroomPhoneNumber}
                        </ContactTypography>
                    )}
                    {dealer?.properties.showroomFaxNumber && (
                        <ContactTypography
                            {...styles.showroomInformation?.showroomFaxNumber}
                            type="fax"
                            targetValue={dealer.properties.showroomFaxNumber}
                            icon={true}
                        >
                            {dealer?.properties.showroomFaxNumber}
                        </ContactTypography>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </Modal>
    );
};

export default ShowroomModal;
